import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Upload,
  FileText,
  Film,
  Radio,
  Play,
  Pause,
  Download,
  RotateCcw,
  CheckCircle,
  Clock,
  Loader,
  ArrowLeft,
  ArrowRight,
  Mic,
  Users,
  BookOpen,
  MessageCircle,
  Zap,
  Globe,
  Headphones,
  Star,
  Volume2,
  FileUp,
  Sparkles,
  Wand2
} from 'lucide-react';

// Get backend URL from environment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Header Component
export const Header = ({ currentStage, onLogoClick, documentData, user, onLogin, onLogout, usageStatus, onUpgrade }) => {
  const getStageTitle = () => {
    switch(currentStage) {
      case 'upload': return 'Upload Document';
      case 'style': return 'Choose Podcast Style';
      case 'processing': return 'Generating Podcast';
      case 'completed': return 'Your Podcast is Ready!';
      default: return 'Commitly';
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black bg-opacity-90 backdrop-blur-md border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <motion.div 
            className="flex items-center cursor-pointer"
            onClick={onLogoClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Radio className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold text-white">Commitly</h1>
            </div>
          </motion.div>
          
          <div className="flex items-center space-x-4">
            {/* Usage Status Display - Only show for logged in users */}
            {user && usageStatus && (
              <div className="hidden md:flex items-center space-x-2 px-3 py-1 bg-gray-800 rounded-full text-xs">
                {usageStatus.subscription_status === 'pro' ? (
                  <span className="text-green-400 font-medium">✨ Pro</span>
                ) : (
                  <span className="text-gray-300">
                    {usageStatus.podcasts_remaining} podcasts left
                  </span>
                )}
              </div>
            )}

            {/* Current Stage */}
            <div className="hidden md:block text-gray-300 text-sm">
              {getStageTitle()}
            </div>
            
            {/* Document Info */}
            {documentData && (
              <div className="hidden lg:flex items-center space-x-2 px-3 py-1 bg-gray-800 rounded-full">
                <FileText className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-300 truncate max-w-32">
                  {documentData.filename}
                </span>
              </div>
            )}

            {/* Authentication Buttons */}
            <div className="flex items-center space-x-3">
              {user ? (
                <div className="flex items-center space-x-3">
                  {user.subscription_status !== 'pro' && onUpgrade && (
                    <button
                      onClick={onUpgrade}
                      className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm font-medium rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-300"
                    >
                      Upgrade to Pro
                    </button>
                  )}
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {user.email.charAt(0).toUpperCase()}
                    </div>
                    <button
                      onClick={onLogout}
                      className="text-gray-400 hover:text-white text-sm"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={onLogin}
                    className="px-4 py-2 text-gray-300 hover:text-white text-sm font-medium transition-colors"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={onLogin}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Sign Up Free
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

// Document Upload Component
export const DocumentUpload = ({ onDocumentUploaded, sessionId }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (sessionId) {
        formData.append('session_id', sessionId);
      }

      const response = await axios.post(`${BACKEND_URL}/api/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      const documentData = {
        ...response.data,
        originalFile: file
      };

      toast.success('Document uploaded successfully!');
      onDocumentUploaded(documentData);
      
    } catch (error) {
      console.error('Upload failed:', error);
      
      if (error.response?.status === 403) {
        const errorDetail = error.response.data.detail;
        if (typeof errorDetail === 'object' && errorDetail.error) {
          toast.error(errorDetail.message || 'Upload limit reached');
          // Usage limit reached - App.js will handle showing auth/upgrade modals
        } else {
          toast.error(error.response.data.detail || 'Upload failed');
        }
      } else {
        toast.error('Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [onDocumentUploaded, sessionId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    multiple: false,
    disabled: uploading
  });

  return (
    <div className="text-center">
      {/* Hero Section */}
      <motion.div 
        className="mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          Transform Documents into 
          <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent"> Podcasts</span>
        </h1>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
          Upload your boring training docs, reports, or any text document. 
          Our AI converts them into engaging, world-class podcast episodes with multiple voices.
        </p>
        
        <div className="flex items-center justify-center space-x-8 text-gray-400 text-sm">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span>PDF, DOCX, TXT</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span>Multiple Voices</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span>14 Podcast Styles</span>
          </div>
        </div>
      </motion.div>

      {/* Upload Area */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="max-w-2xl mx-auto"
      >
        <div
          {...getRootProps()}
          className={`
            relative border-2 border-dashed rounded-xl p-12 transition-all duration-300 cursor-pointer
            ${isDragActive 
              ? 'border-blue-400 bg-blue-500 bg-opacity-10' 
              : 'border-gray-600 hover:border-gray-500 bg-gray-800 bg-opacity-50'
            }
            ${uploading ? 'pointer-events-none opacity-70' : ''}
          `}
        >
          <input {...getInputProps()} />
          
          {uploading ? (
            <div className="text-center">
              <motion.div 
                className="w-16 h-16 mx-auto mb-4 flex items-center justify-center"
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <Loader className="w-8 h-8 text-blue-400" />
              </motion.div>
              <h3 className="text-lg font-medium text-white mb-2">Uploading...</h3>
              <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-400">{uploadProgress}% complete</p>
            </div>
          ) : (
            <div className="text-center">
              <motion.div 
                className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center"
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.2 }}
              >
                <Upload className="w-8 h-8 text-white" />
              </motion.div>
              
              <h3 className="text-xl font-medium text-white mb-2">
                {isDragActive ? 'Drop your document here' : 'Upload Your Document'}
              </h3>
              
              <p className="text-gray-400 mb-4">
                Drag & drop your file here, or click to browse
              </p>
              
              <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <FileText className="w-4 h-4" />
                  <span>PDF</span>
                </div>
                <div className="flex items-center space-x-1">
                  <FileText className="w-4 h-4" />
                  <span>DOCX</span>
                </div>
                <div className="flex items-center space-x-1">
                  <FileText className="w-4 h-4" />
                  <span>TXT</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
      
      {/* Features Preview */}
      <motion.div 
        className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
            <Wand2 className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">AI Script Generation</h3>
          <p className="text-gray-400 text-sm">GPT-4 powered script creation with natural conversation flow</p>
        </div>
        
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
            <Users className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">Multiple Voices</h3>
          <p className="text-gray-400 text-sm">ElevenLabs voices for different speakers and hosts</p>
        </div>
        
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">14 Styles</h3>
          <p className="text-gray-400 text-sm">From Joe Rogan to NPR, choose your perfect format</p>
        </div>
      </motion.div>
    </div>
  );
};

// Style Selector Component
export const StyleSelector = ({ documentData, onStyleSelected, onBack }) => {
  const [selectedStyle, setSelectedStyle] = useState('');

  const podcastStyles = [
    {
      id: 'joe_rogan',
      name: 'Joe Rogan Experience',
      description: 'Casual, long-form conversation with tangents and humor',
      icon: MessageCircle,
      color: 'from-red-500 to-orange-600',
      example: 'Unfiltered discussion with personal anecdotes and deep dives'
    },
    {
      id: 'nyt_daily',
      name: 'The Daily (NYT)',
      description: 'Professional news format with expert analysis',
      icon: Globe,
      color: 'from-blue-500 to-indigo-600',
      example: 'Journalistic approach with clear structure and expert voices'
    },
    {
      id: 'this_american_life',
      name: 'This American Life',
      description: 'Narrative storytelling with acts and themes',
      icon: BookOpen,
      color: 'from-green-500 to-emerald-600',
      example: 'Story-driven format with personal elements and reflection'
    },
    {
      id: 'explainer',
      name: 'Educational Explainer',
      description: 'Clear, accessible education with examples',
      icon: Users,
      color: 'from-purple-500 to-violet-600',
      example: 'Teacher-student style with questions and clarifications'
    },
    {
      id: 'debate',
      name: 'Debate Format',
      description: 'Two experts discussing different viewpoints',
      icon: MessageCircle,
      color: 'from-yellow-500 to-amber-600',
      example: 'Back-and-forth discussion with opposing perspectives'
    },
    {
      id: 'storytelling',
      name: 'Narrative Story',
      description: 'Engaging storytelling with character voices',
      icon: Film,
      color: 'from-pink-500 to-rose-600',
      example: 'Immersive narrative with dramatic elements'
    },
    {
      id: 'stuff_you_know',
      name: 'Stuff You Should Know',
      description: 'Friendly co-hosts explaining topics with humor',
      icon: Users,
      color: 'from-cyan-500 to-blue-600',
      example: 'Educational but fun, like learning with friends'
    },
    {
      id: 'mel_robbins',
      name: 'Mel Robbins Style',
      description: 'High-energy motivational advice',
      icon: Zap,
      color: 'from-orange-500 to-red-600',
      example: 'Direct, actionable advice with motivational energy'
    }
  ];

  const handleStyleSelection = (styleId) => {
    setSelectedStyle(styleId);
  };

  const handleContinue = () => {
    if (selectedStyle) {
      onStyleSelected(selectedStyle);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <motion.div 
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <button 
          onClick={onBack}
          className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Upload</span>
        </button>
        
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Choose Your Podcast Style
        </h1>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-2">
          Select the format that best fits your content and audience
        </p>
        
        {documentData && (
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-800 rounded-full text-sm text-gray-300">
            <FileText className="w-4 h-4" />
            <span>{documentData.filename}</span>
            <span className="text-gray-500">•</span>
            <span>{documentData.estimated_podcast_length}</span>
          </div>
        )}
      </motion.div>

      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {podcastStyles.map((style, index) => {
          const Icon = style.icon;
          const isSelected = selectedStyle === style.id;
          
          return (
            <motion.div
              key={style.id}
              className={`
                relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-300
                ${isSelected 
                  ? 'border-blue-500 bg-blue-500 bg-opacity-10' 
                  : 'border-gray-700 hover:border-gray-600 bg-gray-800 bg-opacity-50'
                }
              `}
              onClick={() => handleStyleSelection(style.id)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSelected && (
                <div className="absolute top-3 right-3">
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                </div>
              )}
              
              <div className={`w-12 h-12 mb-4 bg-gradient-to-r ${style.color} rounded-lg flex items-center justify-center`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              
              <h3 className="text-lg font-semibold text-white mb-2">{style.name}</h3>
              <p className="text-gray-400 text-sm mb-3">{style.description}</p>
              <p className="text-gray-500 text-xs italic">{style.example}</p>
            </motion.div>
          );
        })}
      </motion.div>

      {selectedStyle && (
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <button
            onClick={handleContinue}
            className="inline-flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105"
          >
            <span>Generate Podcast</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </motion.div>
      )}
    </div>
  );
};

// Processing Stage Component
export const ProcessingStage = ({ 
  documentData, 
  selectedStyle, 
  onScriptGenerated, 
  onAudioGenerated, 
  processingStatus,
  onBack,
  sessionId
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [scriptData, setScriptData] = useState(null);
  const [audioId, setAudioId] = useState(null);
  const [error, setError] = useState(null);
  
  const steps = [
    { 
      id: 'script', 
      name: 'Generating Script', 
      description: 'AI is creating your podcast script...',
      icon: FileText 
    },
    { 
      id: 'audio', 
      name: 'Generating Audio', 
      description: 'Converting script to multi-voice audio...',
      icon: Mic 
    },
    { 
      id: 'complete', 
      name: 'Complete!', 
      description: 'Your podcast is ready!',
      icon: CheckCircle 
    }
  ];

  useEffect(() => {
    generateScript();
  }, []);

  const generateScript = async () => {
    try {
      setCurrentStep(0);
      setError(null);
      
      const requestData = {
        document_id: documentData.document_id,
        style: selectedStyle
      };

      if (sessionId) {
        requestData.session_id = sessionId;
      }
      
      const response = await axios.post(`${BACKEND_URL}/api/generate-script`, requestData);

      setScriptData(response.data);
      onScriptGenerated(response.data);
      setCurrentStep(1);
      
      // Start audio generation
      setTimeout(() => generateAudio(response.data.script_id), 1000);
      
    } catch (error) {
      console.error('Script generation failed:', error);
      
      if (error.response?.status === 403) {
        const errorDetail = error.response.data.detail;
        if (typeof errorDetail === 'object' && errorDetail.error) {
          setError(errorDetail.message || 'Usage limit reached');
          toast.error(errorDetail.message || 'Script generation limit reached');
        } else {
          setError('Failed to generate script. Please try again.');
          toast.error('Script generation failed');
        }
      } else {
        setError('Failed to generate script. Please try again.');
        toast.error('Script generation failed');
      }
    }
  };

  const generateAudio = async (scriptId) => {
    try {
      const requestData = {
        script_id: scriptId
      };

      if (sessionId) {
        requestData.session_id = sessionId;
      }

      const response = await axios.post(`${BACKEND_URL}/api/generate-audio`, requestData);

      setAudioId(response.data.audio_id);
      
      // Poll for audio completion
      checkAudioStatus(response.data.audio_id);
      
    } catch (error) {
      console.error('Audio generation failed:', error);
      setError('Failed to generate audio. Please try again.');
      toast.error('Audio generation failed');
    }
  };

  const checkAudioStatus = async (audioId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/audio-status/${audioId}`);
      
      if (response.data.status === 'completed') {
        setCurrentStep(2);
        onAudioGenerated({
          audio_id: audioId,
          ...response.data
        });
        toast.success('Podcast generated successfully!');
      } else {
        // Continue polling
        setTimeout(() => checkAudioStatus(audioId), 3000);
      }
    } catch (error) {
      console.error('Audio status check failed:', error);
      // Continue polling anyway
      setTimeout(() => checkAudioStatus(audioId), 5000);
    }
  };

  const retryGeneration = () => {
    setCurrentStep(0);
    setError(null);
    setScriptData(null);
    setAudioId(null);
    generateScript();
  };

  return (
    <div className="max-w-4xl mx-auto text-center">
      <motion.div 
        className="mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <button 
          onClick={onBack}
          className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Styles</span>
        </button>
        
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Creating Your Podcast
        </h1>
        <p className="text-xl text-gray-300 mb-6">
          Please wait while we generate your podcast...
        </p>
        
        {documentData && (
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-800 rounded-full text-sm text-gray-300 mb-8">
            <FileText className="w-4 h-4" />
            <span>{documentData.filename}</span>
            <span className="text-gray-500">•</span>
            <span className="capitalize">{selectedStyle.replace('_', ' ')}</span>
          </div>
        )}
      </motion.div>

      {error ? (
        <motion.div 
          className="max-w-md mx-auto p-6 bg-red-900 bg-opacity-30 border border-red-700 rounded-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-red-400 mb-4">{error}</div>
          <button
            onClick={retryGeneration}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </motion.div>
      ) : (
        <div className="space-y-8">
          {/* Progress Steps */}
          <div className="flex items-center justify-center space-x-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              
              return (
                <motion.div 
                  key={step.id}
                  className="flex flex-col items-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.2 }}
                >
                  <div className={`
                    w-16 h-16 rounded-full flex items-center justify-center mb-3 transition-all duration-300
                    ${isCompleted 
                      ? 'bg-green-500' 
                      : isActive 
                        ? 'bg-blue-500' 
                        : 'bg-gray-700'
                    }
                  `}>
                    {isActive && !isCompleted ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      >
                        <Loader className="w-8 h-8 text-white" />
                      </motion.div>
                    ) : (
                      <Icon className={`w-8 h-8 ${isCompleted ? 'text-white' : isActive ? 'text-white' : 'text-gray-400'}`} />
                    )}
                  </div>
                  
                  <h3 className={`font-medium ${isActive || isCompleted ? 'text-white' : 'text-gray-400'}`}>
                    {step.name}
                  </h3>
                  <p className="text-sm text-gray-500 text-center max-w-32">
                    {step.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Current Status */}
          <motion.div 
            className="max-w-md mx-auto p-6 bg-gray-800 bg-opacity-50 rounded-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="text-center">
              <h3 className="text-lg font-medium text-white mb-2">
                {steps[currentStep]?.name}
              </h3>
              <p className="text-gray-300 text-sm mb-4">
                {steps[currentStep]?.description}
              </p>
              
              {currentStep < 2 && (
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <motion.div 
                    className="bg-blue-500 h-2 rounded-full"
                    initial={{ width: '0%' }}
                    animate={{ width: `${((currentStep + 1) / 3) * 100}%` }}
                    transition={{ duration: 1 }}
                  />
                </div>
              )}
            </div>
          </motion.div>

          {scriptData && (
            <motion.div 
              className="max-w-2xl mx-auto p-6 bg-gray-800 bg-opacity-30 rounded-xl border border-gray-700"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <h4 className="text-lg font-medium text-white mb-3">Script Preview</h4>
              <div className="text-left text-sm text-gray-300 max-h-40 overflow-y-auto">
                {scriptData.script.split('\n').slice(0, 5).map((line, index) => (
                  <p key={index} className="mb-1">{line}</p>
                ))}
                {scriptData.script.split('\n').length > 5 && (
                  <p className="text-gray-500 italic">...</p>
                )}
              </div>
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
};

// Audio Player Component
export const AudioPlayer = ({ 
  scriptData, 
  audioData, 
  documentData, 
  selectedStyle, 
  onStartNew 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(480); // Default 8 minutes
  const [showScript, setShowScript] = useState(true);
  const [audioAvailable, setAudioAvailable] = useState(false);
  const audioRef = useRef(null);

  // Check if actual audio is available
  useEffect(() => {
    checkAudioAvailability();
  }, [audioData]);

  const checkAudioAvailability = async () => {
    try {
      // Use the new availability check endpoint
      const response = await fetch(`${BACKEND_URL}/api/audio-available/${audioData.audio_id}`);
      if (response.ok) {
        const data = await response.json();
        setAudioAvailable(data.available);
      } else {
        setAudioAvailable(false);
      }
    } catch (error) {
      console.error('Audio availability check failed:', error);
      setAudioAvailable(false);
    }
  };

  const handlePlayPause = () => {
    if (!audioAvailable) {
      toast.error('Audio file is still being processed. Please try downloading the segments.');
      return;
    }

    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(error => {
          console.error('Audio play failed:', error);
          toast.error('Unable to play audio. Please try downloading instead.');
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/download-audio/${audioData.audio_id}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `podcast_${documentData.filename.split('.')[0]}.mp3`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Audio segment downloaded! Full merged audio coming soon.');
      } else {
        toast.error('Audio not ready yet. Individual segments are being generated.');
      }
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed. Audio may still be processing.');
    }
  };

  const simulateProgress = () => {
    // Simulate playback progress for demo purposes when audio isn't available
    if (!audioAvailable && isPlaying) {
      const interval = setInterval(() => {
        setCurrentTime(prev => {
          if (prev >= duration) {
            setIsPlaying(false);
            clearInterval(interval);
            return 0;
          }
          return prev + 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  };

  useEffect(() => {
    const cleanup = simulateProgress();
    return cleanup;
  }, [isPlaying, audioAvailable]);

  return (
    <motion.div 
      className="space-y-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center space-x-4 mb-6">
          <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white">
            Podcast Generated!
          </h1>
        </div>
        
        <div className="flex items-center justify-center space-x-4 text-gray-300 mb-8">
          <div className="flex items-center space-x-2">
            <FileText className="w-4 h-4" />
            <span>{documentData.filename}</span>
          </div>
          <span className="text-gray-500">•</span>
          <div className="flex items-center space-x-2">
            <Radio className="w-4 h-4" />
            <span className="capitalize">{selectedStyle.replace('_', ' ')}</span>
          </div>
          <span className="text-gray-500">•</span>
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4" />
            <span>{Math.round(audioData.total_duration || 8)} min</span>
          </div>
        </div>
      </div>

      {/* Audio Player */}
      <div className="max-w-4xl mx-auto bg-gray-800 rounded-xl p-6">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={handlePlayPause}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
              audioAvailable 
                ? 'bg-blue-500 hover:bg-blue-600' 
                : 'bg-gray-600 hover:bg-gray-700'
            }`}
            title={audioAvailable ? 'Play/Pause' : 'Audio still processing'}
          >
            {isPlaying ? (
              <Pause className="w-6 h-6 text-white" />
            ) : (
              <Play className="w-6 h-6 text-white ml-1" />
            )}
          </button>
          
          <div className="flex-1">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-400">
                {Math.floor(currentTime / 60)}:{(currentTime % 60).toString().padStart(2, '0')}
              </span>
              <span className="text-sm text-gray-400">
                {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}
              </span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2 cursor-pointer">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-100"
                style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              title="Download Audio Segment"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowScript(!showScript)}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              title="Toggle Script"
            >
              <FileText className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Audio element - only if audio is available */}
        {audioAvailable && (
          <audio
            ref={audioRef}
            onLoadedMetadata={() => {
              if (audioRef.current) {
                setDuration(audioRef.current.duration || 480);
              }
            }}
            onTimeUpdate={() => {
              if (audioRef.current) {
                setCurrentTime(audioRef.current.currentTime);
              }
            }}
            onEnded={() => setIsPlaying(false)}
          >
            <source src={`${BACKEND_URL}/api/download-audio/${audioData.audio_id}`} type="audio/mpeg" />
          </audio>
        )}

        {/* Audio Status */}
        <div className="text-sm text-center">
          {audioAvailable ? (
            <span className="text-green-400">✓ Audio ready for playback</span>
          ) : (
            <span className="text-yellow-400">⚠️ Audio segments generated, merging in progress...</span>
          )}
          {audioData.segments_info && (
            <span className="text-gray-400 ml-4">
              {audioData.segments_info.length} voice segments created
            </span>
          )}
        </div>
      </div>

      {/* Script Display */}
      <AnimatePresence>
        {showScript && (
          <motion.div 
            className="max-w-4xl mx-auto bg-gray-800 rounded-xl p-6"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-lg font-medium text-white mb-4 flex items-center space-x-2">
              <FileText className="w-5 h-5" />
              <span>Podcast Script</span>
            </h3>
            <div className="text-gray-300 text-sm leading-relaxed max-h-96 overflow-y-auto">
              {scriptData.script.split('\n').map((line, index) => {
                if (line.trim() === '') return <br key={index} />;
                
                // Check if line contains speaker name
                if (line.includes('[') && line.includes(']:')) {
                  const [speaker, ...dialogueParts] = line.split(']:');
                  const dialogue = dialogueParts.join(']:');
                  
                  return (
                    <p key={index} className="mb-2">
                      <span className="font-medium text-blue-400">
                        {speaker.replace('[', '')}:
                      </span>
                      <span className="ml-2">{dialogue}</span>
                    </p>
                  );
                }
                
                return (
                  <p key={index} className="mb-2">{line}</p>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Actions */}
      <div className="text-center space-y-4">
        <div className="flex justify-center space-x-4">
          <button
            onClick={onStartNew}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition-colors"
          >
            <RotateCcw className="w-5 h-5" />
            <span>Create New Podcast</span>
          </button>
          
          <button
            onClick={handleDownload}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300"
          >
            <Download className="w-5 h-5" />
            <span>Download Audio Segment</span>
          </button>
        </div>
        
        <p className="text-gray-500 text-sm">
          Individual voice segments available for download. Full merged audio coming soon!
        </p>
      </div>
    </motion.div>
  );
};

// Recent Documents Component
export const RecentDocuments = ({ onDocumentSelected }) => {
  const [recentDocs, setRecentDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Make API call non-blocking and optional
    fetchRecentDocuments();
  }, []);

  const fetchRecentDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/documents`, {
        timeout: 3000  // 3 second timeout
      });
      if (response.data && response.data.documents) {
        setRecentDocs(response.data.documents.slice(0, 6)); // Show last 6
      }
    } catch (error) {
      console.warn('Backend unavailable - recent documents not loaded:', error);
      // Don't show error to user, just show empty state
      setRecentDocs([]);
    } finally {
      setLoading(false);
    }
  };

  // Always render interface immediately - don't block on loading
  return (
    <motion.div 
      className="mt-16"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.6 }}
    >
      <h3 className="text-xl font-medium text-white text-center mb-6">
        Recent Documents
      </h3>
      
      {loading && (
        <div className="text-center py-4">
          <Loader className="w-5 h-5 text-gray-400 animate-spin mx-auto" />
          <p className="text-gray-500 text-sm mt-2">Loading recent documents...</p>
        </div>
      )}
      
      {!loading && recentDocs.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-sm">No recent documents yet</p>
          <p className="text-xs text-gray-400">Your uploaded documents will appear here</p>
        </div>
      )}

      {recentDocs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {recentDocs.map((doc, index) => (
            <motion.div
              key={doc.id}
              className="p-4 bg-gray-800 bg-opacity-50 rounded-lg border border-gray-700 hover:border-gray-600 cursor-pointer transition-all duration-300"
              onClick={() => onDocumentSelected(doc)}
              whileHover={{ scale: 1.02 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-500 bg-opacity-20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-blue-400" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-white truncate">
                    {doc.filename}
                  </h4>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(doc.uploaded_at).toLocaleDateString()}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {doc.text_length} characters
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

// Pricing Section Component
export const PricingSection = ({ onBack, onUpgrade }) => {
  return (
    <motion.div 
      className="max-w-4xl mx-auto text-center"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <button 
        onClick={onBack}
        className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to App</span>
      </button>

      <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
        Simple, Transparent Pricing
      </h1>
      <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12">
        Choose the plan that's right for you. Start free, upgrade when you're ready.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {/* Free Plan */}
        <div className="relative p-8 bg-gray-800 bg-opacity-50 rounded-xl border border-gray-700">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-white mb-2">Free</h3>
            <div className="text-4xl font-bold text-white mb-1">$0</div>
            <p className="text-gray-400 mb-6">Perfect for trying out</p>
            
            <div className="space-y-4 text-left mb-8">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-gray-300">2 podcast generations</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-gray-300">3,000 characters processed</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-gray-300">All 14 podcast styles</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-gray-300">Multi-voice AI audio</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-gray-300">Download MP3 files</span>
              </div>
            </div>
            
            <button className="w-full py-3 px-6 bg-gray-600 text-white rounded-lg font-medium opacity-75 cursor-not-allowed">
              Current Plan
            </button>
          </div>
        </div>

        {/* Pro Plan */}
        <div className="relative p-8 bg-gradient-to-b from-blue-500 to-purple-600 bg-opacity-20 rounded-xl border-2 border-blue-500">
          <div className="absolute top-4 right-4">
            <span className="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">
              RECOMMENDED
            </span>
          </div>
          
          <div className="text-center">
            <h3 className="text-2xl font-bold text-white mb-2">Pro</h3>
            <div className="text-4xl font-bold text-white mb-1">
              $49<span className="text-xl text-gray-300">/month</span>
            </div>
            <p className="text-gray-400 mb-6">For power users & businesses</p>
            
            <div className="space-y-4 text-left mb-8">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-gray-200">Unlimited podcast generations</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-gray-200">Unlimited character processing</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-gray-200">Priority processing</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-gray-200">Premium AI voices</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-gray-200">Early access to new features</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-gray-200">Email support</span>
              </div>
            </div>
            
            <button 
              onClick={onUpgrade}
              className="w-full py-3 px-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105"
            >
              Upgrade to Pro
            </button>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mt-16 text-left max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
        
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-white mb-2">What happens when I reach my free limit?</h3>
            <p className="text-gray-400">You'll be prompted to create an account (if you haven't already) or upgrade to Pro for unlimited access.</p>
          </div>
          
          <div>
            <h3 className="text-lg font-medium text-white mb-2">Can I cancel anytime?</h3>
            <p className="text-gray-400">Yes! You can cancel your Pro subscription anytime. You'll continue to have Pro access until the end of your billing period.</p>
          </div>
          
          <div>
            <h3 className="text-lg font-medium text-white mb-2">What file types are supported?</h3>
            <p className="text-gray-400">We support PDF, DOCX, and TXT files. Our AI processes the text and creates engaging podcast scripts.</p>
          </div>
          
          <div>
            <h3 className="text-lg font-medium text-white mb-2">How long does it take to generate a podcast?</h3>
            <p className="text-gray-400">Typically 2-5 minutes depending on document length. Script generation is fast, and audio creation takes a bit longer for quality results.</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Authentication Modal Component
export const AuthModal = ({ isOpen, onClose, onLogin }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (isSignUp) {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setIsLoading(false);
          return;
        }
        
        if (password.length < 8) {
          setError('Password must be at least 8 characters');
          setIsLoading(false);
          return;
        }

        // Sign up
        const response = await axios.post(`${BACKEND_URL}/api/auth/signup`, {
          email,
          password
        });

        toast.success('Account created! Please check your email to verify your account.');
        onLogin(response.data.user, response.data.access_token);
        onClose();
        
      } else {
        // Sign in
        const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
          email,
          password
        });

        toast.success('Welcome back!');
        onLogin(response.data.user, response.data.access_token);
        onClose();
      }
    } catch (error) {
      console.error('Auth error:', error);
      setError(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4" onClick={handleModalClick}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">
            {isSignUp ? 'Create Account' : 'Welcome Back'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="bg-red-900 bg-opacity-50 border border-red-700 text-red-300 p-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
              required
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
              required
              placeholder="••••••••"
              minLength={8}
            />
          </div>

          {isSignUp && (
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                required
                placeholder="••••••••"
                minLength={8}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50"
          >
            {isLoading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign In')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-blue-400 hover:text-blue-300"
          >
            {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
          </button>
        </div>

        {isSignUp && (
          <div className="mt-4 text-xs text-gray-400 text-center">
            By creating an account, you agree to our{' '}
            <button className="text-blue-400 hover:text-blue-300">Terms of Service</button>
            {' '}and{' '}
            <button className="text-blue-400 hover:text-blue-300">Privacy Policy</button>
          </div>
        )}
      </div>
    </div>
  );
};

// Upgrade Modal Component
export const UpgradeModal = ({ isOpen, onClose, usageStatus, user, authToken }) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleUpgrade = async () => {
    setIsLoading(true);
    
    try {
      const headers = {};
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await axios.post(`${BACKEND_URL}/api/payments/create-checkout`, {
        success_url: `${window.location.origin}?payment=success`,
        cancel_url: `${window.location.origin}?payment=cancelled`
      }, { headers });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
      
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error('Failed to start checkout. Please try again.');
      setIsLoading(false);
    }
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4" onClick={handleModalClick}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">Upgrade to Pro</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            ✕
          </button>
        </div>

        <div className="text-center mb-6">
          <div className="text-4xl font-bold text-white mb-2">
            $49<span className="text-xl text-gray-300">/month</span>
          </div>
          <p className="text-gray-400">Unlock unlimited podcast generation</p>
        </div>

        {usageStatus && (
          <div className="bg-gray-700 p-4 rounded-lg mb-6">
            <h3 className="text-white font-medium mb-2">Your Current Usage:</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Podcasts Created:</span>
                <span className="text-white">{usageStatus.podcasts_created}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Podcasts Remaining:</span>
                <span className="text-white">
                  {usageStatus.podcasts_remaining === -1 ? 'Unlimited' : usageStatus.podcasts_remaining}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3 mb-6">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-gray-200">Unlimited podcast generations</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-gray-200">Priority processing</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-gray-200">Premium AI voices</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-gray-200">Early access to new features</span>
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Maybe Later
          </button>
          <button
            onClick={handleUpgrade}
            disabled={isLoading}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-300 disabled:opacity-50"
          >
            {isLoading ? 'Redirecting...' : 'Upgrade Now'}
          </button>
        </div>

        <p className="text-xs text-gray-400 text-center mt-4">
          Cancel anytime. Secure payment processed by Stripe.
        </p>
      </div>
    </div>
  );
};
